import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Flame, Loader2, FileSignature, ListChecks, FileText, ShieldCheck, Award, Megaphone } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { crmService } from "@/services/crmService";
import { leadTypeLabel } from "@/config/crmOptions";

/**
 * Global search — returns real, workspace-scoped Contacts and Leads. Never
 * fabricates results for modules that don't exist yet.
 */
export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ contacts: [], leads: [], tasks: [], documents: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (query.trim().length < 2) { setResults({ contacts: [], leads: [], tasks: [], documents: [] }); return; }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await crmService.search(query.trim());
        if (active) setResults(data);
      } catch { /* ignore */ } finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, open]);

  const go = (path) => { setOpen(false); setQuery(""); navigate(path); };
  const hasResults = results.contacts.length > 0 || results.leads.length > 0 || (results.transactions || []).length > 0 || (results.tasks || []).length > 0 || (results.documents || []).length > 0 || (results.licenses || []).length > 0 || (results.memberships || []).length > 0 || (results.marketing || []).length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary"
        aria-label="Search AgentHQ"
      >
        <Search className="h-4 w-4" />
        <span className="hidden lg:inline">Search AgentHQ…</span>
        <kbd className="ml-2 hidden rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium lg:inline">⌘K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[15%] max-w-xl translate-y-0 gap-0 p-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Search AgentHQ</DialogTitle>
          <DialogDescription className="sr-only">Search your contacts and leads.</DialogDescription>
          <div className="flex items-center gap-3 border-b border-border px-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts, leads and transactions…"
              className="h-14 border-0 px-0 text-base shadow-none focus-visible:ring-0" data-testid="global-search-input" />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {query.trim().length < 2 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">Type at least 2 characters to search your contacts, leads and transactions.</p>
            ) : !hasResults && !loading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="global-search-empty">No contacts, leads or transactions match “{query}”.</p>
            ) : (
              <>
                {results.contacts.length > 0 && (
                  <div className="mb-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contacts</p>
                    {results.contacts.map((c) => (
                      <button key={c.id} onClick={() => go(`/crm/${c.id}`)} data-testid={`search-contact-${c.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{c.first_name} {c.last_name}</span>
                        <span className="text-muted-foreground">{c.email || c.phone || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
                {results.leads.length > 0 && (
                  <div>
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Leads</p>
                    {results.leads.map((l) => (
                      <button key={l.id} onClick={() => go(l.contact?.id ? `/crm/${l.contact.id}` : "/pipeline")} data-testid={`search-lead-${l.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <Flame className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{l.contact?.first_name} {l.contact?.last_name}</span>
                        <span className="text-muted-foreground">{leadTypeLabel(l.lead_type)} · {l.stage?.name || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.transactions || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Transactions</p>
                    {results.transactions.map((t) => (
                      <button key={t.id} onClick={() => go(`/transactions/${t.id}`)} data-testid={`search-transaction-${t.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <FileSignature className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{t.property_address_line1}</span>
                        <span className="text-muted-foreground">{t.contact?.first_name} {t.contact?.last_name} · {t.status}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.tasks || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Tasks</p>
                    {results.tasks.map((t) => (
                      <button key={t.id} onClick={() => go(t.related?.type === "transaction" ? `/transactions/${t.related.id}` : t.related?.contact_id ? `/crm/${t.related.contact_id}` : "/tasks")} data-testid={`search-task-${t.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <ListChecks className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{t.title}</span>
                        <span className="text-muted-foreground">{t.status}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.documents || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Documents</p>
                    {results.documents.map((d) => (
                      <button key={d.id} onClick={() => go("/documents")} data-testid={`search-document-${d.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{d.file_name}</span>
                        <span className="uppercase text-muted-foreground">{d.file_type}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.licenses || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Licenses</p>
                    {results.licenses.map((l) => (
                      <button key={l.id} onClick={() => go("/compliance")} data-testid={`search-license-${l.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{l.state} · {l.license_number}</span>
                        <span className="capitalize text-muted-foreground">{l.derived_status}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.memberships || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Memberships</p>
                    {results.memberships.map((m) => (
                      <button key={m.id} onClick={() => go("/memberships")} data-testid={`search-membership-${m.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <Award className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{m.organization_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {(results.marketing || []).length > 0 && (
                  <div className="mt-2">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Marketing</p>
                    {results.marketing.map((m) => (
                      <button key={m.id} onClick={() => go(`/marketing/studio/${m.id}`)} data-testid={`search-marketing-${m.id}`}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                        <Megaphone className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{m.title}</span>
                        <span className="capitalize text-muted-foreground">{m.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
